import { Button, Tag, Tooltip } from "antd";

/* ─── Types ──────────────────────────────────────────────────────────── */

export interface TrainerBranch { _id?: string; name?: string; }
export interface RewardField { fieldName?: string; amount?: number; remark?: string; }

export interface AddonIncentiveRecord {
  id?:               string;
  trainerId?:        string;
  trainerName?:      string;
  trainerPhone?:     string;
  trainerBranches?:  TrainerBranch[];
  fromDate?:         string;
  toDate?:           string;
  noOfSessions?:     number;
  totalClients?:     number;
  revenueGenerated?: number;
  slabPercentage?:   number;
  incentiveAmount?:  number;
  tdsAmount?:        number;
  gstAmount?:        number;
  deduction?:        number;
  rewardAmount?:     number;
  rewardFields?:     RewardField[];
  finalTotal?:       number;
  paidAmount?:       number;
  dueAmount?:        number;
  status?:           string;
  generatedByName?:  string;
  paidByNames?:      string[];
  lastPaidAt?:       string;
  generatedAt?:      string;
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

const inr = (n?: number) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function formatDate(date?: string): string {
  if (!date) return '-';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '-';
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
}

// Mirrors incentiveHistory's statusKey — backend's `status` string varies
// ("unpaid", "partialpaid", "fullpaid" …), normalize to a fixed key.
const statusKey = (status?: string) => {
  const v = (status || '').toLowerCase();
  if (v.includes('partial')) return 'partial';
  if (v.includes('unpaid'))  return 'unpaid';
  return 'paid';
};

const statusColor = (status?: string) => {
  const key = statusKey(status);
  return key === 'partial' ? 'orange' : key === 'unpaid' ? 'red' : 'green';
};

const statusLabel = (status?: string) => {
  const key = statusKey(status);
  return key === 'partial' ? 'Partial Paid' : key === 'unpaid' ? 'Unpaid' : 'Full Paid';
};

/* ─── Columns ────────────────────────────────────────────────────────── */

export const getAddonIncentiveColumns = (onPay: (record: AddonIncentiveRecord) => void) => [
  {
    title:     'Trainer',
    key:       'trainer',
    width:     170,
    render:    (_: unknown, record: AddonIncentiveRecord) => (
      <div>
        <div>{record.trainerName || '-'}</div>
        {record.trainerPhone && <div style={{ fontSize: 12, color: '#999' }}>{record.trainerPhone}</div>}
      </div>
    ),
  },
  {
    title:  'Branch',
    key:    'branch',
    width:  150,
    render: (_: unknown, record: AddonIncentiveRecord) => {
      const branches = record.trainerBranches || [];
      if (branches.length === 0) return '-';
      const firstBranch    = branches[0]?.name || 'N/A';
      const remainingCount = branches.length - 1;
      if (remainingCount <= 0) return firstBranch;
      const allBranchNames = branches.map(b => b?.name).filter(Boolean).join(', ');
      return (
        <Tooltip title={allBranchNames}>
          <span>{firstBranch} +{remainingCount}</span>
        </Tooltip>
      );
    },
  },
  {
    title:  'Period',
    key:    'period',
    width:  160,
    render: (_: unknown, record: AddonIncentiveRecord) => (
      <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div><span style={{ color: '#999' }}>From:</span> <strong>{formatDate(record.fromDate)}</strong></div>
        <div><span style={{ color: '#999' }}>To:</span> <strong>{formatDate(record.toDate)}</strong></div>
      </div>
    ),
  },
  { title: 'Sessions', dataIndex: 'noOfSessions', key: 'noOfSessions', width: 100, align: 'center' as const, render: (v?: number) => v ?? 0 },
  { title: 'Clients',  dataIndex: 'totalClients', key: 'totalClients', width: 100, align: 'center' as const, render: (v?: number) => v ?? 0 },
  { title: 'Revenue',  dataIndex: 'revenueGenerated', key: 'revenueGenerated', width: 130, render: (v?: number) => inr(v) },
  { title: 'Slab %',   dataIndex: 'slabPercentage',   key: 'slabPercentage',   width: 90,  align: 'center' as const, render: (v?: number) => `${v ?? 0}%` },
  { title: 'Incentive Amount', dataIndex: 'incentiveAmount', key: 'incentiveAmount', width: 140, render: (v?: number) => inr(v) },
  { title: 'TDS', dataIndex: 'tdsAmount', key: 'tdsAmount', width: 100, render: (v?: number) => v ? <span style={{ color: '#ff4d4f' }}>− {inr(v)}</span> : '-' },
  { title: 'GST', dataIndex: 'gstAmount', key: 'gstAmount', width: 100, render: (v?: number) => v ? <span style={{ color: '#ff4d4f' }}>− {inr(v)}</span> : '-' },
  {
    title:  'Deduction',
    dataIndex: 'deduction',
    key:    'deduction',
    width:  110,
    render: (v?: number) => v ? <span style={{ color: '#ff4d4f' }}>− {inr(v)}</span> : '-',
  },
  {
    title:  'Reward',
    key:    'rewardAmount',
    width:  110,
    render: (_: unknown, record: AddonIncentiveRecord) => {
      if (!record.rewardAmount) return '-';
      const remarks = (record.rewardFields || [])
        .filter(f => f.remark)
        .map(f => `${f.fieldName || 'Reward'}: ${f.remark}`)
        .join('\n');
      const value = <span style={{ color: '#52c41a' }}>+ {inr(record.rewardAmount)}</span>;
      return remarks ? <Tooltip title={remarks}>{value}</Tooltip> : value;
    },
  },
  {
    title:  'Net Payable',
    dataIndex: 'finalTotal',
    key:    'finalTotal',
    width:  130,
    render: (v?: number) => <strong>{inr(v)}</strong>,
  },
  {
    title:     'Status',
    dataIndex: 'status',
    key:       'status',
    width:     120,
    align:     'center' as const,
    render:    (v?: string) => <Tag color={statusColor(v)}>{statusLabel(v)}</Tag>,
  },
  {
    title:  'Generated',
    key:    'generated',
    width:  160,
    render: (_: unknown, record: AddonIncentiveRecord) => (
      <div>
        <div>{formatDate(record.generatedAt)}</div>
        {record.generatedByName && <div style={{ fontSize: 12, color: '#999' }}>{record.generatedByName}</div>}
      </div>
    ),
  },
  {
    title:  'Paid',
    dataIndex: 'paidAmount',
    key:    'paidAmount',
    width:  120,
    render: (v?: number) => v ? <span style={{ color: '#52c41a' }}>{inr(v)}</span> : inr(0),
  },
  {
    title:  'Due',
    dataIndex: 'dueAmount',
    key:    'dueAmount',
    width:  120,
    render: (v?: number) => v ? <span style={{ color: '#ff4d4f' }}>{inr(v)}</span> : inr(0),
  },
  {
    title:  'Paid By',
    key:    'paidBy',
    width:  150,
    render: (_: unknown, record: AddonIncentiveRecord) => {
      if (!record.paidByNames?.length) return '-';
      return (
        <div>
          <div>{record.paidByNames.join(', ')}</div>
          {record.lastPaidAt && <div style={{ fontSize: 12, color: '#999' }}>{formatDate(record.lastPaidAt)}</div>}
        </div>
      );
    },
  },
  {
    title:  'Actions',
    key:    'actions',
    width:  90,
    fixed:  'right' as const,
    align:  'center' as const,
    render: (_: unknown, record: AddonIncentiveRecord) => {
      if (statusKey(record.status) === 'paid' || !((record.dueAmount ?? 0) > 0)) return null;
      return (
        <Button size="small" type="primary" onClick={() => onPay(record)}>
          Pay
        </Button>
      );
    },
  },
];
